// Cookie-consent data model — shared between client (persist + apply) and
// server (validate the POST /api/consent body, attribute an audit row).
//
// The ConsentRecord is the firm's *proof of consent* (UK/EU GDPR
// accountability): it captures WHAT was consented to, the policy VERSION at
// the time, the visitor's REGIME/region, and the METHOD (one-click accept,
// reject, granular save, or an auto opt-out triggered by a Global Privacy
// Control signal). Stored first-party in the `omega_consent` cookie + a
// localStorage mirror — never anything secret, so it is intentionally NOT
// httpOnly (the client JS must read it to decide whether to show the banner).

import { z } from 'zod';

/** Bump when the shape below changes — invalidates stored records and
 *  re-prompts every visitor. */
export const CONSENT_SCHEMA_VERSION = 1;

/** Re-prompt window. 6 months mirrors common ICO/CNIL guidance. */
export const CONSENT_MAX_AGE_DAYS = 180;

export const CONSENT_COOKIE_NAME = 'omega_consent';

export const ConsentCategorySchema = z.enum([
  'necessary',   // strictly necessary — always true, cannot be disabled (ePrivacy Art. 5(3) exempt)
  'analytics',   // GA4 / measurement                → analytics_storage
  'marketing',   // Ads / remarketing / pixels       → ad_storage + ad_user_data + ad_personalization
  'functional',  // non-essential preferences        → functionality_storage + personalization_storage
]);
export type ConsentCategory = z.infer<typeof ConsentCategorySchema>;

/** Which privacy regime governs this visitor — drives opt-in vs opt-out. */
export const ConsentRegimeSchema = z.enum([
  'gdpr',     // EU/EEA + UK + (treated-as) UAE PDPL / SG PDPA → opt-in
  'ccpa',     // US (esp. California) → opt-out, honour Global Privacy Control
  'pdpl',     // UAE Federal Decree-Law 45/2021 → treated opt-in
  'pdpa',     // Singapore PDPA → treated opt-in
  'unknown',  // could not detect → strictest (opt-in)
]);
export type ConsentRegime = z.infer<typeof ConsentRegimeSchema>;

export const ConsentMethodSchema = z.enum([
  'accept_all',
  'reject_all',
  'custom',
  'gpc_auto',   // auto opt-out from a browser Global Privacy Control signal
]);
export type ConsentMethod = z.infer<typeof ConsentMethodSchema>;

export const ConsentCategoriesSchema = z.object({
  necessary: z.literal(true),  // can never be false
  analytics: z.boolean(),
  marketing: z.boolean(),
  functional: z.boolean(),
});
export type ConsentCategories = z.infer<typeof ConsentCategoriesSchema>;

export const ConsentRecordSchema = z.object({
  v: z.number().int(),               // CONSENT_SCHEMA_VERSION at write time
  policy_version: z.string(),        // cmsLegal.version at write time (re-consent on bump — cf. REQ-012)
  method: ConsentMethodSchema,
  regime: ConsentRegimeSchema,
  region: z.string(),                // best-effort ISO country / 'US-CA' / 'unknown'
  categories: ConsentCategoriesSchema,
  gpc: z.boolean(),                  // was a Global Privacy Control signal present?
  updated_at: z.string(),            // ISO 8601
});
export type ConsentRecord = z.infer<typeof ConsentRecordSchema>;

export const ALL_GRANTED: ConsentCategories = {
  necessary: true, analytics: true, marketing: true, functional: true,
};
export const ALL_DENIED: ConsentCategories = {
  necessary: true, analytics: false, marketing: false, functional: false,
};
